import { css } from '@emotion/react';
import React, { Key } from 'react';
import { Checkbox } from '../checkbox';
import { Stack } from '../stack';
import { StyledTableCol } from './styles';
import { TableCol } from './TableCol';
import { TableRow } from './TableRow';
import {
  DataTableProps,
  SelectRowsAction,
  DataTableColumnGroupType,
  DataTableColumnType,
  DataTableRowSelectionRecord,
} from './types';
import { getFixedMergeColumn, getGroupColumns } from './utils';

export interface DataTableHeaderProps<DataType>
  extends Pick<
    DataTableProps<DataType>,
    | 'columns'
    | 'rows'
    | 'sortBy'
    | 'sortDirection'
    | 'onSortBy'
    | 'selectableRows'
    | 'expandableButton'
    | 'renderExpandedRow'
    | 'hideSelectAll'
    | 'resize'
  > {
  stickyHeader?: boolean;
  stickyOffset?: number;
  allSelected?: boolean;
  selectedRows?: Map<Key, DataTableRowSelectionRecord<DataType>>;
  onSelectedRows?: (action: SelectRowsAction<DataType>) => void;
}

export const DataTableHeader = <DataType extends object>({
  columns,
  rows,
  stickyHeader,
  stickyOffset,
  sortBy,
  sortDirection,
  allSelected,
  selectedRows,
  selectableRows,
  expandableButton,
  renderExpandedRow,
  hideSelectAll,
  resize,
  onSelectedRows,
  onSortBy,
}: DataTableHeaderProps<DataType>) => {
  const computedGroupColumns = getGroupColumns({ columns });
  const SelectionColumn = () => {
    const isColumnGroup = columns.some((column) => {
      const columnGroup = (column as DataTableColumnGroupType<DataType>).columnGroup;
      return columnGroup && columnGroup.length > 0;
    });
    const selectionProps = {
      stickyHeader,
      stickyOffset,
      ...(columns[0]?.fixed && { fixed: true, fixedColWidth: 0 }),
      ...(isColumnGroup && { rowSpan: 2 }),
    };
    const indeterminate = selectedRows && selectedRows.size > 0 && !allSelected;

    return (
      <StyledTableCol className={selectionProps.fixed ? 'pds-table-cell-fixed' : undefined} {...selectionProps}>
        {hideSelectAll ? null : (
          <Stack direction='column' align='center'>
            <Checkbox
              name='all-row-select'
              indeterminate={indeterminate}
              disabled={rows.length === 0 || rows.every((row) => row.disabled || !(row.selectable ?? true))}
              checked={allSelected}
              onChange={() => onSelectedRows?.({ type: 'SELECT_ROWS', rows })}
              css={css`
                justify-content: center;
              `}
            />
          </Stack>
        )}
      </StyledTableCol>
    );
  };

  const ExpandableColumn = () => {
    const isColumnGroup = columns.some((column) => {
      const columnGroup = (column as DataTableColumnGroupType<DataType>).columnGroup;
      return columnGroup && columnGroup.length > 0;
    });
    const expandableProps = {
      stickyHeader,
      stickyOffset,
      ...(columns[0]?.fixed && { fixed: true, fixedColWidth: selectableRows ? 48 : 0 }),
      ...(isColumnGroup && { rowSpan: 2 }),
    };

    return (
      <StyledTableCol className={expandableProps.fixed ? 'pds-table-cell-fixed' : undefined} {...expandableProps} />
    );
  };

  return (
    <>
      <TableRow>
        {!!selectableRows && <SelectionColumn />}
        {renderExpandedRow && expandableButton && <ExpandableColumn />}
        {columns.map((column, index) => {
          const baseColumn = column as DataTableColumnType<DataType>;
          const columnGroup = (column as DataTableColumnGroupType<DataType>).columnGroup;
          if (baseColumn) {
            const fixedColumnProps =
              columnGroup?.length > 0
                ? {
                    fixed: false,
                  }
                : {
                    ...(baseColumn.fixed && {
                      fixedColWidth: getFixedMergeColumn(
                        columns,
                        !!selectableRows,
                        index,
                        !!renderExpandedRow && Boolean(expandableButton),
                      ),
                    }),
                  };

            const columnGroupProps = columnGroup
              ? { ...(columnGroup?.length > 0 && { colSpan: columnGroup?.length }) }
              : { rowSpan: 2 };

            const tableColProps = {
              stickyHeader,
              stickyOffset,
              sortBy,
              sortDirection,
              resize,
              ...baseColumn,
              ...columnGroupProps,
              ...fixedColumnProps,
            };

            return (
              <TableCol
                key={index}
                {...tableColProps}
                onClick={() => tableColProps.sortable && onSortBy?.(tableColProps.id)}
                sortProps={{
                  isSorted: tableColProps.sortBy === tableColProps.id && tableColProps.sortDirection !== 'default',
                  // @ts-expect-error: TS2322
                  sortDirection: tableColProps.sortDirection,
                }}
              >
                {tableColProps.text}
              </TableCol>
            );
          }
        })}
      </TableRow>
      {computedGroupColumns.length > 0 && (
        <TableRow>
          {computedGroupColumns.map((column, index) => {
            const lastThHasRowspan =
              (columns[columns.length - 1] as DataTableColumnGroupType<DataType>).columnGroup?.length > 0;

            const subColumn = column as DataTableColumnType<DataType>;
            if (subColumn) {
              const subTableColProps = {
                stickyHeader,
                stickyOffset,
                sortBy,
                sortDirection,
                resize,
                ...subColumn,
                ...{ groupColumn: stickyHeader },
              };

              const showRightBorder = index === computedGroupColumns.length - 1 && !lastThHasRowspan;
              return (
                <TableCol
                  key={index}
                  {...subTableColProps}
                  onClick={() => subTableColProps.sortable && onSortBy?.(subTableColProps.id)}
                  sortProps={{
                    isSorted:
                      subTableColProps.sortBy === subTableColProps.id && subTableColProps.sortDirection !== 'default',
                    // @ts-expect-error: TS2322
                    sortDirection: subTableColProps.sortDirection,
                  }}
                  showRightBorder={showRightBorder}
                >
                  {subTableColProps.text}
                </TableCol>
              );
            }
          })}
        </TableRow>
      )}
    </>
  );
};
